import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { FileText, Upload, Download, Trash2 } from 'lucide-react';

const ComplaintsTab = ({ authAxios }) => {
  const [complaintsPolicyInfo, setComplaintsPolicyInfo] = useState(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const pdfInputRef = useRef(null);

  useEffect(() => {
    fetchComplaintsPolicy();
  }, [authAxios]);

  const fetchComplaintsPolicy = async () => {
    try {
      const response = await authAxios.get('/admin/complaints-policy');
      if (response.data.filename) setComplaintsPolicyInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch complaints policy:', error);
    }
  };

  const handlePdfFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('Please select a PDF file'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('File size must be less than 10MB'); return; }
    
    setUploadingPdf(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        await authAxios.post('/admin/complaints-policy', { pdf_base64: base64, filename: file.name });
        toast.success('Complaints policy uploaded successfully!');
        fetchComplaintsPolicy();
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload complaints policy');
    } finally {
      setUploadingPdf(false);
      if (pdfInputRef.current) pdfInputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    try {
      await authAxios.delete('/admin/complaints-policy');
      setComplaintsPolicyInfo(null);
      toast.success('Complaints policy deleted');
    } catch (error) {
      toast.error('Failed to delete complaints policy');
    }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="complaints-tab">
      <div className="bg-[#111111] border border-white/5 p-6">
        <h2 className="font-mono font-bold mb-6 flex items-center gap-2">
          <FileText className="w-5 h-5 text-[#D4AF37]" />
          Complaints Policy PDF
        </h2>
        <p className="text-white/60 text-sm mb-6">Upload your company's complaints policy PDF. This will be available for download on the complaints page.</p>
        
        {complaintsPolicyInfo ? (
          <div className="space-y-4">
            <div className="p-4 bg-[#0A0A0A] border border-[#D4AF37]/30 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-[#D4AF37]" />
                <div>
                  <p className="font-mono text-sm">{complaintsPolicyInfo.filename}</p>
                  <p className="text-white/50 text-xs">Uploaded: {new Date(complaintsPolicyInfo.uploaded_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a href={complaintsPolicyInfo.pdf_url} target="_blank" rel="noopener noreferrer" className="p-2 text-white/50 hover:text-[#D4AF37]" title="Download PDF"><Download className="w-5 h-5" /></a>
                <button onClick={handleDelete} className="p-2 text-white/50 hover:text-red-400" title="Delete PDF"><Trash2 className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="text-center pt-4 border-t border-white/5">
              <p className="text-white/50 text-sm mb-3">Replace with a new file:</p>
              <input ref={pdfInputRef} type="file" accept=".pdf" onChange={handlePdfFileChange} className="hidden" id="pdf-upload-replace" />
              <label htmlFor="pdf-upload-replace" className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-white/70 font-mono text-sm hover:bg-white/5 cursor-pointer">
                <Upload className="w-4 h-4" />{uploadingPdf ? 'Uploading...' : 'Upload New PDF'}
              </label>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 border-2 border-dashed border-white/10 hover:border-[#D4AF37]/30 transition-colors">
            <FileText className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <p className="text-white/50 font-mono text-sm mb-4">No complaints policy uploaded</p>
            <input ref={pdfInputRef} type="file" accept=".pdf" onChange={handlePdfFileChange} className="hidden" id="pdf-upload" />
            <label htmlFor="pdf-upload" className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black font-mono text-sm hover:bg-[#D4AF37]/90 cursor-pointer">
              <Upload className="w-4 h-4" />{uploadingPdf ? 'Uploading...' : 'Upload PDF'}
            </label>
            <p className="text-white/30 text-xs mt-3">Max file size: 10MB</p>
          </div>
        )}
      </div>
      
      <div className="bg-[#111111] border border-white/5 p-6">
        <h3 className="font-mono font-bold mb-4 text-sm">External Redress Link</h3>
        <p className="text-white/60 text-sm mb-3">The complaints page includes a link to The Property Ombudsman for external redress:</p>
        <a href="https://www.thepims.co.uk" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] font-mono text-sm hover:underline">
          https://www.thepims.co.uk
        </a>
      </div>
    </div>
  );
};

export default ComplaintsTab;
